import { appConfirm } from '../../services/appDialogs';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { BookOpen, Loader2, Plus, Save, Sparkles, Trash2, Settings2, X } from 'lucide-react';
import {
  fetchStoryStructureState,
  generateStoryStructureCards,
  saveStoryStructureState,
} from '../../services/storyStructureService';
import { Button } from '../ui/Button';
import { Textarea } from '../ui/Textarea';
import { Input } from '../ui/Input';
import './StoryStructureWorkbench.css';

// 全剧 15 节拍（与后端 defaultSaveTheCatNodes 对应；前端兜底用，正常以后端返回的 nodes 为准）
const FALLBACK_BEATS = [
  ['opening', '开场画面', '建立主角原始处境和世界状态。'],
  ['theme', '主题呈现', '让主角遇到核心价值问题。'],
  ['setup', '铺垫', '交代人物、关系、欲望、缺口。'],
  ['catalyst', '催化事件', '打破原有生活秩序。'],
  ['debate', '犹豫与选择', '主角是否进入新局面。'],
  ['break-into-two', '进入第二幕', '主角被迫或主动迈入新世界。'],
  ['b-story', '副线/关系线', '建立情感、盟友或镜像人物。'],
  ['fun-games', '游戏时间', '兑现类型看点，连续制造爽点或压力。'],
  ['midpoint', '中点反转', '局势升级，胜利或失败变得不可逆。'],
  ['bad-guys-close-in', '压力逼近', '反派、误会、代价持续压迫。'],
  ['all-is-lost', '一无所有', '主角遭遇最低点。'],
  ['dark-night', '灵魂黑夜', '主角意识到真正要改变什么。'],
  ['break-into-three', '进入第三幕', '新方案、新同盟或新认知出现。'],
  ['finale', '高潮解决', '正面解决核心冲突。'],
  ['final-image', '结尾画面', '展示主角变化和余钩。'],
];

const makeFallbackNodes = () =>
  FALLBACK_BEATS.map(([id, name, purpose]) => ({ id, name, purpose, assignments: [] }));

const emptyState = (projectId) => ({
  projectId,
  stateKey: 'story_structure',
  sourceText: '',
  activeBoardId: 'global',
  cards: [],
  boards: {
    global: {
      id: 'global',
      type: 'global',
      title: '全剧故事骨架',
      subtitle: '把故事卡排进全局节拍。',
      nodes: makeFallbackNodes(),
    },
  },
  meta: {},
});

const normalizeState = (raw, projectId) => {
  const state = { ...emptyState(projectId), ...(raw || {}) };
  state.cards = Array.isArray(state.cards) ? state.cards : [];
  state.boards = state.boards && typeof state.boards === 'object' ? state.boards : {};
  state.meta = state.meta && typeof state.meta === 'object' ? state.meta : {};
  if (!state.boards.global) {
    state.boards.global = { id: 'global', type: 'global', title: '全剧故事骨架', subtitle: '', nodes: makeFallbackNodes() };
  }
  if (!Array.isArray(state.boards.global.nodes) || state.boards.global.nodes.length === 0) {
    state.boards.global.nodes = makeFallbackNodes();
  }
  state.boards.global.nodes = state.boards.global.nodes.map((n) => ({
    ...n,
    assignments: Array.isArray(n.assignments) ? n.assignments.map(String) : [],
  }));
  state.activeBoardId = 'global';
  return state;
};

const cardTitle = (card) => card?.title || card?.summary || '未命名故事卡';
const compactText = (value, fallback = '未填写') => {
  const t = Array.isArray(value) ? value.join('、') : String(value || '').trim();
  return t || fallback;
};
const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (!value) return [];
  return String(value).split(/[、,，/|]/).map((s) => s.trim()).filter(Boolean);
};

export default function StoryStructureWorkbench({ projectId }) {
  const [state, setState] = useState(() => emptyState(projectId));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [message, setMessage] = useState('');
  const [editingCardId, setEditingCardId] = useState('');
  const [beatPickerCardId, setBeatPickerCardId] = useState(''); // 正在为哪张卡选节拍

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchStoryStructureState(projectId)
      .then((data) => { if (!cancelled) setState(normalizeState(data, projectId)); })
      .catch((err) => { if (!cancelled) setMessage(err.message || '故事骨架加载失败'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [projectId]);

  const cards = state.cards || [];
  const beats = state.boards?.global?.nodes || [];
  const cardMap = useMemo(() => {
    const m = new Map();
    cards.forEach((c) => m.set(String(c.id), c));
    return m;
  }, [cards]);

  // 卡片 -> 所在节拍 id
  const cardBeat = useMemo(() => {
    const m = new Map();
    beats.forEach((b) => (b.assignments || []).forEach((id) => m.set(String(id), b.id)));
    return m;
  }, [beats]);

  const unassignedCards = cards.filter((c) => !cardBeat.has(String(c.id)));

  const updateState = useCallback((updater) => {
    setState((prev) => normalizeState(typeof updater === 'function' ? updater(prev) : updater, projectId));
  }, [projectId]);

  const updateSourceText = (sourceText) => updateState((prev) => ({ ...prev, sourceText }));

  const updateCard = (cardId, patch) => {
    updateState((prev) => ({
      ...prev,
      cards: prev.cards.map((c) => (String(c.id) === String(cardId) ? { ...c, ...patch } : c)),
    }));
  };

  // 把卡片移动到某个节拍（先从所有节拍移除，再加入目标）
  const assignCardToBeat = (cardId, beatId) => {
    updateState((prev) => {
      const global = prev.boards.global;
      const nodes = (global.nodes || []).map((node) => {
        const assignments = (node.assignments || []).filter((id) => String(id) !== String(cardId));
        if (node.id === beatId) assignments.push(String(cardId));
        return { ...node, assignments };
      });
      return { ...prev, boards: { ...prev.boards, global: { ...global, nodes } } };
    });
    setBeatPickerCardId('');
  };

  const handleSave = async () => {
    setSaving(true); setMessage('');
    try {
      const saved = await saveStoryStructureState(projectId, state);
      setState(normalizeState(saved, projectId));
      setMessage('已保存故事骨架');
    } catch (err) {
      setMessage(err.message || '保存失败');
    } finally { setSaving(false); }
  };

  const handleGenerateCards = async () => {
    if (!state.sourceText?.trim()) { setMessage('请先粘贴小说或剧本文本'); return; }
    setGenerating(true); setMessage('');
    try {
      const next = await generateStoryStructureCards(projectId, { ...state, sourceText: state.sourceText, maxCards: 36 });
      setState(normalizeState(next, projectId));
      setMessage(`AI 已拆出 ${next?.cards?.length || 0} 张故事卡并自动排进骨架，可点卡片微调`);
    } catch (err) {
      setMessage(err.message || 'AI 拆故事卡失败');
    } finally { setGenerating(false); }
  };

  const handleAddCard = (beatId) => {
    const id = `manual-${Date.now()}`;
    const card = { id, title: '新故事卡', summary: '', characters: [], location: '未明确', time: '未明确', conflict: '', infoChange: '', sourceRange: '', sourceExcerpt: '', beat: beatId || '' };
    updateState((prev) => {
      let next = { ...prev, cards: [...prev.cards, card] };
      if (beatId) {
        const nodes = (prev.boards.global.nodes || []).map((n) =>
          n.id === beatId ? { ...n, assignments: [...(n.assignments || []), id] } : n);
        next = { ...next, boards: { ...prev.boards, global: { ...prev.boards.global, nodes } } };
      }
      return next;
    });
    setEditingCardId(id);
  };

  const handleDeleteCard = async (cardId) => {
    if (!(await appConfirm('确认删除这张故事卡？'))) return;
    updateState((prev) => {
      const nodes = (prev.boards.global.nodes || []).map((n) => ({
        ...n,
        assignments: (n.assignments || []).filter((id) => String(id) !== String(cardId)),
      }));
      return {
        ...prev,
        cards: prev.cards.filter((c) => String(c.id) !== String(cardId)),
        boards: { ...prev.boards, global: { ...prev.boards.global, nodes } },
      };
    });
    if (String(editingCardId) === String(cardId)) setEditingCardId('');
  };

  const editingCard = editingCardId ? cardMap.get(String(editingCardId)) : null;
  const totalAssigned = cards.length - unassignedCards.length;
  const hasCards = cards.length > 0;

  if (loading) {
    return (
      <div className="story2-page story2-loading">
        <Loader2 className="animate-spin" size={20} />
        <span>正在加载故事骨架...</span>
      </div>
    );
  }

  return (
    <div className="story2-page">
      {/* 顶部操作栏 */}
      <header className="story2-header">
        <div className="story2-title">
          <BookOpen size={18} />
          <div>
            <h1>故事骨架</h1>
            <p>三步搞定：① 粘贴原文 → ② AI 自动拆卡排骨架 → ③ 看一眼微调后保存</p>
          </div>
        </div>
        <div className="story2-actions">
          <Button variant="secondary" onClick={handleSave} loading={saving}>
            <Save size={15} /> 保存
          </Button>
          <Button onClick={handleGenerateCards} loading={generating}>
            <Sparkles size={15} /> {hasCards ? '重新 AI 拆卡' : 'AI 一键拆卡排骨架'}
          </Button>
        </div>
      </header>

      {/* 步骤进度条 */}
      <div className="story2-steps">
        <span className={state.sourceText?.trim() ? 'done' : 'active'}>① 粘贴原文</span>
        <span className="arrow">→</span>
        <span className={hasCards ? 'done' : state.sourceText?.trim() ? 'active' : ''}>② AI 拆卡排骨架</span>
        <span className="arrow">→</span>
        <span className={hasCards ? 'active' : ''}>③ 微调并保存</span>
      </div>

      {message && <div className="story2-message">{message}</div>}

      <div className="story2-body">
        {/* 第1步：原文 */}
        <section className="story2-source">
          <div className="story2-section-title">第 1 步 · 粘贴小说或剧本原文</div>
          <Textarea
            value={state.sourceText || ''}
            onChange={(e) => updateSourceText(e.target.value)}
            placeholder="把小说原文、章节节选或已有剧本粘贴到这里，然后点右上角「AI 一键拆卡排骨架」。"
            className="story2-source-textarea"
          />
        </section>

        {/* 未排入的卡片（一般为空，AI 会自动排好） */}
        {unassignedCards.length > 0 && (
          <section className="story2-unassigned">
            <div className="story2-section-title">待归位卡片（点卡片选节拍）</div>
            <div className="story2-card-row">
              {unassignedCards.map((card) => (
                <MiniCard key={card.id} card={card} onPick={() => setBeatPickerCardId(String(card.id))} onEdit={() => setEditingCardId(String(card.id))} />
              ))}
            </div>
          </section>
        )}

        {/* 第2/3步：骨架节拍（纵向，每个节拍显示其卡片） */}
        <section className="story2-skeleton">
          <div className="story2-section-title">
            故事骨架 · 全剧 15 节拍
            {hasCards && <span className="story2-count">已排 {totalAssigned}/{cards.length} 张卡</span>}
          </div>

          {!hasCards ? (
            <div className="story2-empty">
              <Sparkles size={28} />
              <p>还没有故事卡。粘贴原文后点「AI 一键拆卡排骨架」，AI 会自动把剧情拆成故事卡并排进下面的节拍里。</p>
            </div>
          ) : (
            <div className="story2-beat-list">
              {beats.map((beat, idx) => {
                const beatCards = (beat.assignments || []).map((id) => cardMap.get(String(id))).filter(Boolean);
                return (
                  <div key={beat.id} className="story2-beat">
                    <div className="story2-beat-head">
                      <span className="story2-beat-no">{String(idx + 1).padStart(2, '0')}</span>
                      <div className="story2-beat-meta">
                        <strong>{beat.name}</strong>
                        <span>{beat.purpose}</span>
                      </div>
                      <button type="button" className="story2-add-card" onClick={() => handleAddCard(beat.id)} title="在此节拍加卡">
                        <Plus size={14} />
                      </button>
                    </div>
                    <div className="story2-card-row">
                      {beatCards.length === 0 ? (
                        <div className="story2-beat-empty">（暂无卡片）</div>
                      ) : beatCards.map((card) => (
                        <MiniCard key={card.id} card={card} onPick={() => setBeatPickerCardId(String(card.id))} onEdit={() => setEditingCardId(String(card.id))} />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>

      {/* 节拍选择弹窗：点卡片后选它属于哪个节拍 */}
      {beatPickerCardId && (
        <div className="story2-modal-mask animate-in fade-in duration-150" onClick={() => setBeatPickerCardId('')}>
          <div className="story2-modal story2-beat-picker animate-in fade-in zoom-in-95 duration-200" onClick={(e) => e.stopPropagation()}>
            <div className="story2-modal-head">
              <span>把「{cardTitle(cardMap.get(String(beatPickerCardId)))}」放到哪个节拍？</span>
              <button type="button" onClick={() => setBeatPickerCardId('')}><X size={16} /></button>
            </div>
            <div className="story2-beat-options">
              {beats.map((b, i) => (
                <button
                  key={b.id}
                  type="button"
                  className={cardBeat.get(String(beatPickerCardId)) === b.id ? 'active' : ''}
                  onClick={() => assignCardToBeat(beatPickerCardId, b.id)}
                >
                  <span className="no">{String(i + 1).padStart(2, '0')}</span>
                  <span className="nm">{b.name}</span>
                  <span className="pp">{b.purpose}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* 卡片编辑弹窗 */}
      {editingCard && (
        <div className="story2-modal-mask animate-in fade-in duration-150" onClick={() => setEditingCardId('')}>
          <div className="story2-modal story2-card-editor animate-in fade-in zoom-in-95 duration-200" onClick={(e) => e.stopPropagation()}>
            <div className="story2-modal-head">
              <span>编辑故事卡 · {editingCard.id}</span>
              <button type="button" onClick={() => setEditingCardId('')}><X size={16} /></button>
            </div>
            <div className="story2-editor-body">
              <Input label="标题" value={editingCard.title || ''} onChange={(e) => updateCard(editingCard.id, { title: e.target.value })} />
              <Textarea label="摘要" value={editingCard.summary || ''} onChange={(e) => updateCard(editingCard.id, { summary: e.target.value })} className="story2-editor-textarea" />
              <Input label="人物" value={compactText(editingCard.characters, '')} onChange={(e) => updateCard(editingCard.id, { characters: toArray(e.target.value) })} />
              <div className="story2-editor-two">
                <Input label="地点" value={editingCard.location || ''} onChange={(e) => updateCard(editingCard.id, { location: e.target.value })} />
                <Input label="时间" value={editingCard.time || ''} onChange={(e) => updateCard(editingCard.id, { time: e.target.value })} />
              </div>
              <Textarea label="冲突/压力点" value={editingCard.conflict || ''} onChange={(e) => updateCard(editingCard.id, { conflict: e.target.value })} className="story2-editor-textarea small" />
              <Textarea label="信息变化" value={editingCard.infoChange || ''} onChange={(e) => updateCard(editingCard.id, { infoChange: e.target.value })} className="story2-editor-textarea small" />
            </div>
            <div className="story2-editor-foot">
              <button type="button" className="story2-danger-link" onClick={() => handleDeleteCard(editingCard.id)}>
                <Trash2 size={13} /> 删除这张卡
              </button>
              <Button variant="secondary" onClick={() => { setEditingCardId(''); setBeatPickerCardId(String(editingCard.id)); }}>
                <Settings2 size={14} /> 改节拍
              </Button>
              <Button onClick={() => setEditingCardId('')}>完成</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MiniCard({ card, onPick, onEdit }) {
  return (
    <div className="story2-mini-card">
      <button type="button" className="story2-mini-main" onClick={onEdit} title="点击编辑">
        <strong>{cardTitle(card)}</strong>
        {card.summary && <span>{card.summary}</span>}
      </button>
      <button type="button" className="story2-mini-beat" onClick={onPick} title="改节拍">
        <Settings2 size={12} />
      </button>
    </div>
  );
}
